/**
 * Créer un cabinet — chunk 9.AS.1, super_admin seulement.
 *
 * ## Pourquoi ce dialogue existe
 *
 * `POST /v1/tenants` existe depuis longtemps et n'avait **aucune porte dans
 * l'interface** : créer un cabinet demandait un appel en ligne de commande.
 * C'est le geste qui ouvre tout le reste — un cabinet, puis ses utilisateurs,
 * puis ses dossiers — et il ne pouvait pas être fait par la personne qui en a
 * besoin.
 *
 * ## Ce qu'il fait après la création
 *
 * Il **bascule immédiatement la portée** sur le cabinet créé. Sans ça, on
 * viendrait de créer un espace et on continuerait de regarder ailleurs — la
 * confusion garantie. Le cache de requêtes est vidé pour la même raison.
 *
 * ## Le champ qui mérite un avertissement
 *
 * L'identifiant court est **immuable** : il sert d'adresse et de clé de
 * résolution. Le dire avant la saisie évite de devoir l'expliquer après.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useCreateTenant } from "../../../lib/queries/tenants";
import { setTenantScope } from "../../../lib/tenantScope";
import { toast } from "../../../lib/toast";

/** Minuscules, chiffres, tirets — la forme que l'API acceptera. */
function versSlug(nom: string): string {
  return nom
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

export function CreateTenantDialog({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const queryClient = useQueryClient();
  const creer = useCreateTenant();
  const titreId = useId();
  const champNom = useRef<HTMLInputElement | null>(null);

  const [nom, setNom] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouche, setSlugTouche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    queueMicrotask(() => champNom.current?.focus());
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !envoi) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, envoi]);

  // L'identifiant se déduit du nom tant que personne ne l'a touché : la plupart
  // des gens n'ont aucune raison d'y penser, et ceux qui en ont une le corrigent.
  const slugEffectif = slugTouche ? slug : versSlug(nom);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    if (nom.trim().length < 2 || slugEffectif.length < 2) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const cree = await creer.mutateAsync({
        name: nom.trim(),
        slug: slugEffectif,
        locale: lang,
      });
      // On regarde ce qu'on vient de créer — sinon on aurait créé un espace et
      // continué à travailler ailleurs.
      const selected = setTenantScope(cree.id);
      if (selected) queryClient.clear();
      else toast.error(t("membership.wait_write"));
      toast.success(t("tenant_scope.created"));
      onClose();
    } catch (err) {
      setErreur(
        err instanceof ApiException
          ? getApiErrorMessage(err, lang) || t("errors.generic")
          : t("errors.generic"),
      );
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titreId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!envoi) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titreId} className="text-base font-semibold text-slate-900">
            {t("tenant_scope.create_title")}
          </h2>
        </header>

        <form onSubmit={soumettre} className="space-y-4 px-5 py-5">
          <p className="text-sm text-slate-600">{t("tenant_scope.create_hint")}</p>

          <div>
            <label htmlFor="cabinet-nom" className="label">
              {t("tenant_scope.field_name")}
            </label>
            <input
              id="cabinet-nom"
              ref={champNom}
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              disabled={envoi}
              minLength={2}
              maxLength={200}
              required
              autoComplete="organization"
              className="input"
            />
          </div>

          <div>
            <label htmlFor="cabinet-slug" className="label">
              {t("tenant_scope.field_slug")}
            </label>
            <input
              id="cabinet-slug"
              value={slugEffectif}
              onChange={(e) => {
                setSlugTouche(true);
                setSlug(versSlug(e.target.value));
              }}
              disabled={envoi}
              minLength={2}
              maxLength={50}
              required
              spellCheck={false}
              autoComplete="off"
              aria-describedby="cabinet-slug-hint"
              className="input font-mono"
            />
            <p id="cabinet-slug-hint" className="mt-1 text-xs text-slate-500">
              {t("tenant_scope.field_slug_hint")}
            </p>
          </div>

          <div aria-live="polite">
            {erreur && (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {erreur}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={envoi}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={envoi || nom.trim().length < 2 || slugEffectif.length < 2}
              aria-busy={envoi}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {envoi ? t("app.loading") : t("tenant_scope.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
